import { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  ManufacturingProduct,
  PRODUCTION_STATE_COLUMNS,
  ProductionOrderWithWorkOrders,
  WorkOrderInline,
} from '../../types/manufacturing';

interface Props {
  orders: ProductionOrderWithWorkOrders[];
  products: ManufacturingProduct[];
  loading: boolean;
}

// Open/in-flight states stay expanded by default; terminal states stay
// collapsed so they don't dominate the view.
const OPEN_STATES = new Set(['draft', 'confirmed', 'progress', 'to_close']);

const STATE_COLOR: Record<
  string,
  'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'
> = {
  draft: 'default',
  confirmed: 'info',
  progress: 'primary',
  to_close: 'warning',
  done: 'success',
  cancel: 'error',
};

function formatDate(d: string | null): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}

function WorkOrderChips({ wos }: { wos: WorkOrderInline[] }) {
  if (wos.length === 0)
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {wos.slice(0, 6).map((wo) => (
        <Tooltip key={wo.id} title={`${wo.name} (${wo.state})`}>
          <Chip
            size="small"
            label={wo.name.length > 12 ? `${wo.name.slice(0, 12)}…` : wo.name}
            color={
              wo.state === 'done' ? 'success' : wo.state === 'progress' ? 'primary' : 'default'
            }
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Tooltip>
      ))}
      {wos.length > 6 && (
        <Chip size="small" label={`+${wos.length - 6}`} sx={{ fontSize: '0.65rem', height: 20 }} />
      )}
    </Stack>
  );
}

function OrdersTable({
  rows,
  productNameById,
}: {
  rows: ProductionOrderWithWorkOrders[];
  productNameById: Map<string, string>;
}) {
  return (
    <TableContainer>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 100 }}>MO</TableCell>
            <TableCell sx={{ minWidth: 200 }}>Product</TableCell>
            <TableCell sx={{ minWidth: 160 }}>Progress</TableCell>
            <TableCell sx={{ minWidth: 110 }}>Planned start</TableCell>
            <TableCell sx={{ minWidth: 110 }}>Deadline</TableCell>
            <TableCell sx={{ minWidth: 110 }}>State</TableCell>
            <TableCell>Work orders</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((o) => {
            const pct =
              o.product_qty > 0 ? Math.min(100, (o.qty_produced / o.product_qty) * 100) : 0;
            return (
              <TableRow key={o.id} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{o.name}</TableCell>
                <TableCell>
                  <Tooltip title={productNameById.get(o.product_id) ?? '(unknown product)'}>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 240,
                      }}
                    >
                      {productNameById.get(o.product_id) ?? '(unknown product)'}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 80 }}>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ minWidth: 60 }}>
                      {o.qty_produced} / {o.product_qty}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>{formatDate(o.date_planned_start ?? o.date_start)}</TableCell>
                <TableCell>{formatDate(o.date_planned_finished ?? o.date_finished)}</TableCell>
                <TableCell>
                  <Chip size="small" label={o.state} color={STATE_COLOR[o.state] ?? 'default'} />
                </TableCell>
                <TableCell>
                  <WorkOrderChips wos={o.work_orders} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function ProductionKanban({ orders, products, loading }: Props) {
  const productNameById = useMemo(
    () => new Map(products.map((p) => [p.id, p.default_code ?? p.name])),
    [products]
  );

  const grouped = useMemo(() => {
    const known = new Set(PRODUCTION_STATE_COLUMNS.map((c) => c.state));
    const buckets: Record<string, ProductionOrderWithWorkOrders[]> = {};
    for (const col of PRODUCTION_STATE_COLUMNS) buckets[col.state] = [];
    const others: ProductionOrderWithWorkOrders[] = [];
    for (const o of orders) {
      if (known.has(o.state)) buckets[o.state].push(o);
      else others.push(o);
    }
    return { buckets, others };
  }, [orders]);

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const col of PRODUCTION_STATE_COLUMNS) init[col.state] = OPEN_STATES.has(col.state);
    return init;
  });
  const toggle = (state: string) => setExpanded((prev) => ({ ...prev, [state]: !prev[state] }));

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }} data-testid="manufacturing-kanban">
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">Production orders</Typography>
        <Typography variant="caption" color="text.secondary">
          {orders.length} total
        </Typography>
      </Stack>
      {loading && orders.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : orders.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No production orders. Run the manufacturing snapshot to populate.
        </Typography>
      ) : null}
      {loading && orders.length > 0 && <LinearProgress sx={{ mb: 1 }} />}

      {PRODUCTION_STATE_COLUMNS.map((col) => {
        const rows = grouped.buckets[col.state];
        if (rows.length === 0) return null;
        return (
          <Accordion
            key={col.state}
            expanded={!!expanded[col.state]}
            onChange={() => toggle(col.state)}
            disableGutters
            sx={{ '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Chip size="small" label={col.label} color={STATE_COLOR[col.state] ?? 'default'} />
                <Typography variant="body2" color="text.secondary">
                  {rows.length} order{rows.length === 1 ? '' : 's'}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <OrdersTable rows={rows} productNameById={productNameById} />
            </AccordionDetails>
          </Accordion>
        );
      })}

      {grouped.others.length > 0 && (
        <Accordion disableGutters sx={{ '&:before': { display: 'none' }, mt: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Chip size="small" label="other" color="warning" />
              <Typography variant="body2" color="text.secondary">
                {grouped.others.length} order{grouped.others.length === 1 ? '' : 's'} in
                un-categorised states
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <OrdersTable rows={grouped.others} productNameById={productNameById} />
          </AccordionDetails>
        </Accordion>
      )}
    </Paper>
  );
}
